import type { ClientFlowAction } from '~/graphql/types.client';
import generateActionId from '../generateActionId';
import { FlowAction } from '~/graphql/types';
import updateDeepLinkIds from '../updateDeepLinkIds';
import sortTopological from './utils/sortTopological';
import { reportAndThrow } from '~/util/reportAndThrow';
import { mapNotNull } from '~/util/array';

/**
 * Clones a set of selected actions, generating new IDs and remapping
 * internal parent/child references. Actions whose parents are all
 * outside the selection get `source` as their primary parent.
 * The copied selection must form a single connected branch with exactly
 * one in-branch root action.
 *
 * The returned array is topologically sorted so that root actions
 * (those with no in-branch parents) come first. This ensures the
 * first element can be correctly wired to the paste target.
 *
 * @param branch - The list of selected actions
 * @param source - Parent id to assign to actions with no in-branch parents
 */
const resetAndCloneBranch = ({
  branch,
  source,
  preserveInternalSecondaryParents = false,
}: {
  branch: Array<ClientFlowAction>;
  source: string;
  /** When true, secondary parents that are inside the copied branch are kept
   *  and remapped to the new IDs. This should only be enabled when pasting
   *  onto a leaf (empty node) so that internal portal relationships are
   *  preserved within the cloned branch. */
  preserveInternalSecondaryParents?: boolean;
}): Array<ClientFlowAction> => {
  if (branch.length === 0) return [];

  const branchIds = new Set(branch.map(a => a.id));
  const rootActions = branch.filter(action => {
    if (action.__typename === 'FlowV2_Action_Start') return false;
    if (!('parentIds' in action)) return true;

    return !action.parentIds.some(parentId => branchIds.has(parentId));
  });

  if (rootActions.length !== 1) {
    return reportAndThrow('Copied branch must have exactly one root action');
  }

  // Process the branch from roots to leaves so parent remapping is deterministic.
  const sorted = sortTopological(branch, branchIds);

  // Map every original action id to a fresh id up-front, before the cloning
  // loop starts. This way, when an action references another action (via
  // parentIds, trueChildId, etc.) the target's new id is already available
  // regardless of processing order.
  const idMap = new Map<string, string>();
  for (const action of sorted) {
    idMap.set(action.id, generateActionId());
  }

  return sorted.map((action): ClientFlowAction => {
    const originalId = action.id;
    const newId = idMap.get(originalId)!;

    const base: ClientFlowAction = {
      ...action,
      id: newId,
      _v: 1,
      _copiedFromId: originalId,
    };

    if ('Statistics' in base) {
      delete base.Statistics;
    }

    if ('label' in base) {
      delete base.label;
    }

    if ('childrenIds' in base) {
      // Clear children — the caller is responsible for wiring them up after paste.
      base.childrenIds = [];
    }

    if ('parentIds' in base && action.__typename !== 'FlowV2_Action_Start') {
      const primaryParentId = action.parentIds[0];
      const newPrimaryParentId =
        primaryParentId && branchIds.has(primaryParentId)
          ? idMap.get(primaryParentId)!
          : source;

      // Cloned actions must only keep their primary parent by default.
      // Secondary parents represent existing portal/merge relationships and
      // should not be copied onto the pasted branch itself — unless the
      // secondary parent is also inside the copied branch (internal portal)
      // and we are pasting onto a leaf.
      const secondaryParentIds = action.parentIds.slice(1);
      const remappedInternalSecondaryParents = preserveInternalSecondaryParents
        ? mapNotNull(
            secondaryParentIds.filter(id => branchIds.has(id)),
            id => idMap.get(id),
          )
        : [];

      base.parentIds = [
        newPrimaryParentId,
        ...remappedInternalSecondaryParents.filter(
          id => id !== newPrimaryParentId,
        ),
      ];
    }

    if (
      action.actionType === FlowAction.IfElse &&
      base.__typename === 'FlowV2_Action_IfElse'
    ) {
      // Update the true/false child ids to the new ids.
      base.trueChildId =
        action.trueChildId && branchIds.has(action.trueChildId)
          ? idMap.get(action.trueChildId)
          : null;
      base.falseChildId =
        action.falseChildId && branchIds.has(action.falseChildId)
          ? idMap.get(action.falseChildId)
          : null;
    }

    if (
      action.__typename === 'FlowV2_Action_SendEmail_Plain' &&
      base.__typename === 'FlowV2_Action_SendEmail_Plain' &&
      action.deepLinks.length > 0
    ) {
      // Replaces the deep link ids with new ones, mutates the action in place
      updateDeepLinkIds({ mutableNewAction: base });
    }

    return base as ClientFlowAction;
  });
};

export default resetAndCloneBranch;
